import {
  BusinessEquityLineItem,
  MonetaryAmountDataPointValue,
  Ownership,
  TenantsInCommon,
} from '../../generated/graphql';
import { getBusinessOwnerName } from '../../Owner';
import { useTranslation } from 'react-i18next';
import { FinancialSummaryTableRow } from '../../UI';
import { displayRelativeDateFromNow } from '../../util';
import {  ownedValueDataPointValue } from '../../DataPoint';
import { getLoanBalanceNegative } from '../../Loan';

export const useBusinessEquityDetails = () => {
  const { t } = useTranslation('pages');
  const formatOwnershipDetails = (ownership: Ownership): string | undefined => {
    return (ownership as TenantsInCommon).interests
      ?.map((interest) => {
        return getBusinessOwnerName(interest);
      })
      .filter((value) => value)
      .join(',');
  };

  const getOwnershipDescription = (ownership: Ownership) => {
    const ownershipNames = `${formatOwnershipDetails(ownership)}`;
    return (
      (!!ownershipNames && `${t('business-term-element-detail-view-details-table-owned-by')}: ${ownershipNames}`) || ''
    );
  };

  const getRows = ({ business, loans: businessLoans, ownedCashAccounts, ownedQualifiedInvestments, ownedNonQualifiedInvestments, ownedRealEstate }: BusinessEquityLineItem): FinancialSummaryTableRow[] => {
    const rows: FinancialSummaryTableRow[] = [
      {
        label: t('business-term-element-detail-view-details-table-estimated-value'),
        amount: ownedValueDataPointValue(business.ownedValue),
        amountLabel: displayRelativeDateFromNow(business.value.latestDataPoint?.dateTime),
      },
    ];

    // Business-owned cash accounts
    if (ownedCashAccounts?.length > 0) {
      for (const ca of ownedCashAccounts) {
        rows.push({
          label: ca.name,
          amount: ca.ownedValue,
          amountLabel: displayRelativeDateFromNow(ca.value.latestDataPoint?.dateTime),
        });
      }
    }

    // Business-owned qualified investments
    if (ownedQualifiedInvestments?.length > 0) {
      for (const inv of ownedQualifiedInvestments) {
        rows.push({
          label: inv.name,
          amount: inv.ownedValue,
          amountLabel: displayRelativeDateFromNow(inv.value.latestDataPoint?.dateTime),
        });
      }
    }

    // Business-owned non-qualified investments
    if (ownedNonQualifiedInvestments?.length > 0) {
      for (const inv of ownedNonQualifiedInvestments) {
        rows.push({
          label: inv.name,
          amount: inv.ownedValue,
          amountLabel: displayRelativeDateFromNow(inv.value.latestDataPoint?.dateTime),
        });
      }
    }

    // Business-owned real estate
    if (ownedRealEstate?.length > 0) {
      for (const re of ownedRealEstate) {
        rows.push({
          label: re.name,
          amount: re.ownedValue,
          amountLabel: displayRelativeDateFromNow(re.value.latestDataPoint?.dateTime),
        });
      }
    }

    // Loans (as negative)
    if (businessLoans?.length > 0) {
      for (const loan of businessLoans) {
        rows.push({
          amountLabel:
            (loan.balance.latestDataPoint?.dateTime &&
              displayRelativeDateFromNow(loan.balance.latestDataPoint?.dateTime)) ||
            '',
          amount: getLoanBalanceNegative(loan),
          label: loan.name,
        });
      }
    } else {
      rows.push({
        label: t('business-term-element-detail-view-details-table-no-loans'),
        amountLabel: displayRelativeDateFromNow(business.value.latestDataPoint?.dateTime),
        amount: {
          currencyCode: (business.value.latestDataPoint?.data as MonetaryAmountDataPointValue | undefined)?.value
            ?.currencyCode ?? 'USD',
          value: 0,
        },
      });
    }

    return rows;
  };

  return {
    getOwnershipDescription,
    getRows,
    tableHeader: t('business-term-element-detail-view-details-table-header'),
    totalLabel: t('business-term-element-detail-view-details-table-total-equity'),
  };
};
